import {
  MaxButton,
  Title,
  Field,
  Input,
  RouteFeeButton,
  InputContainer,
  DropdownContainer,
  ButtonContainer,
  StyledAlertIcon,
  StrategyAndLeverageContainer,
} from './Form.styles';
import Card from '@components/common/Card';
import { useState, useEffect, useMemo } from 'react';
import Button from '@components/common/Button';
import { ArrowLeftRight } from '@components/icons';
import { dappStore, positionStore } from '@components/store';
import {
  useAddresses,
  useStableCoinApprovalsLoaded,
  useApprovedArch,
  useApprovedDai,
  useApprovedUsdc,
  useApprovedUsdt,
  useBalanceArch,
  useBalanceDai,
  useBalanceUsdc,
  useBalanceUsdt,
  useCurrentError,
  useMinimumDepositIncreased,
  useOusdCollateralMinimum,
  useArchApprovalLoaded,
  useLevMultiplier,
  useNumOfCycles,
} from '@components/store/getters';
import { DropdownComponent } from '@components/common/Dropdown';
import { StableCoinOption } from 'pages/open-position/index.page';
import UsdtIcon from '../../../public/usdt.svg';
import UsdcIcon from '../../../public/usdc.svg';
import DaiIcon from '../../../public/dai.svg';
import OusdIcon from '../../../public/ousd.svg';
import TokensFromWallet from '../TokensFromWallet';
import { isValidInputValue } from 'utils/isValidInputValue';
import { useWeb3Methods } from 'hooks/useWeb3Methods';
import { useAccount } from 'wagmi';
import { ErrorMessage } from './components/ErrorMessage';
import Tooltip from '@components/common/Tooltip';
import { roundNumber } from 'utils/roundNumber';

interface FormProps {
  setDeposit: (amount: string) => void;
  deposit: string;
  estimatedPosition: number;
  selectedStableCoin?: StableCoinOption;
  setSelectedStableCoin?: (option: Partial<StableCoinOption>) => void;
  selectedStrategy?: StableCoinOption;
  setSelectedStrategy?: (option: Partial<StableCoinOption>) => void;
  selectedLeverage?: StableCoinOption;
  setSelectedLeverage?: (option: Partial<StableCoinOption>) => void;
  tokenFeeInArch?: number;
  setUseArchFromWallet: (checked?: boolean) => void;
  useArchFromWallet: boolean;
  collateral?: number;
}

export type ErrorType = 'depositGreaterThanBalance' | 'depositLessThanMinimum';

export default function Form({
  deposit,
  setDeposit,
  selectedStableCoin,
  setSelectedStableCoin,
  selectedStrategy,
  setSelectedStrategy,
  selectedLeverage,
  setSelectedLeverage,
  tokenFeeInArch,
  setUseArchFromWallet,
  useArchFromWallet,
  collateral,
}: FormProps) {
  const [formError, setFormError] = useState(false);
  const [errorType, setErrorType] = useState(null);
  const [approvalStableCoinLoading, setApprovalStableCoinLoading] = useState(false);
  const [approvalArchLoading, setApprovalArchLoading] = useState(false);
  const [openPositionLoading, setOpenPositionLoading] = useState(false);
  const addresses = useAddresses();
  const usdcBalance = useBalanceUsdc();
  const usdtBalance = useBalanceUsdt();
  const daiBalance = useBalanceDai();
  const balanceArch = Number(useBalanceArch());
  const minimumDeposit = useOusdCollateralMinimum();
  const minimumDepositIncreased = useMinimumDepositIncreased();
  const currentError = useCurrentError();
  const { approveToken, updateApprovalStatus, openPosition } = useWeb3Methods();
  const approvedUsdt = useApprovedUsdt();
  const approvedUsdc = useApprovedUsdc();
  const approvedDai = useApprovedDai();
  const approvedArch = useApprovedArch();
  const stableCoinApprovalsLoaded = useStableCoinApprovalsLoaded();
  const archApprovalLoaded = useArchApprovalLoaded();
  const leverageMultiplier = useLevMultiplier();
  const { isConnected } = useAccount();
  const numOfCycles = useNumOfCycles();

  const maxHandler = () => {
    setErrorType(null);
    switch (selectedStableCoin.value) {
      case 'usdc':
        setDeposit(usdcBalance?.toString() || '0');
        return;
      case 'dai':
        setDeposit(daiBalance?.toString() || '0');
        return;
      default:
        setDeposit(usdtBalance?.toString() || '0');
        return;
    }
  };

  const handleDepositChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (isValidInputValue(value)) {
      setDeposit(value);
      setErrorType(null);
      if (currentError) dappStore.setState({ currentError: null });
    }
  };

  const openRouteFeeModal = () => {
    dappStore.setState({ showRouteAndFeesModal: true });
  };

  const stableCoinOptions = useMemo(() => {
    return [
      {
        label: 'USDT',
        value: 'usdt',
        address: addresses?.usdtAddress,
        icon: <UsdtIcon />,
        config: { withDescription: true, type: 'stableCoin' },
      },
      {
        label: 'USDC',
        value: 'usdc',
        address: addresses?.usdcAddress,
        icon: <UsdcIcon />,
        config: { withDescription: true, type: 'stableCoin' },
      },
      {
        label: 'DAI',
        value: 'dai',
        address: addresses?.daiAddress,
        icon: <DaiIcon />,
        config: { withDescription: true, type: 'stableCoin' },
      },
    ];
  }, [addresses, usdcBalance, usdtBalance, daiBalance]);

  const leverageMultiplierFormatted = useMemo(() => {
    if (leverageMultiplier) return roundNumber(leverageMultiplier + 1, 2);
  }, [leverageMultiplier]);

  const strategyOptions = useMemo(() => {
    return [{ value: 'ousd', label: 'OUSD Strategy', icon: <OusdIcon /> }];
  }, []);

  const leverageOptions = useMemo(() => {
    if (leverageMultiplierFormatted)
      return [
        {
          value: leverageMultiplierFormatted,
          label: `x${leverageMultiplierFormatted}`,
          config: { withDescription: true, type: 'leverage' },
        },
      ];

    return [
      {
        value: 0,
        label: 'x0',
        config: { withDescription: true, type: 'leverage' },
      },
    ];
  }, [leverageMultiplierFormatted]);

  const depositGreaterThanBalance = useMemo(() => {
    const depositAmount = Number(deposit);
    switch (selectedStableCoin?.value) {
      case 'usdc':
        return depositAmount > Number(usdcBalance);
      case 'dai':
        return depositAmount > Number(daiBalance);
      default:
        return depositAmount > Number(usdtBalance);
    }
  }, [deposit, selectedStableCoin]);

  const stableHasBeenApproved = useMemo(() => {
    if (selectedStableCoin && stableCoinApprovalsLoaded) {
      const depositAmount = Number(deposit);
      switch (selectedStableCoin.value) {
        case 'usdc':
          const approvedUsdcAmount = Number(approvedUsdc);
          return approvedUsdcAmount > 0 && approvedUsdcAmount >= depositAmount;
        case 'dai':
          const approvedDaiAmount = Number(approvedDai);
          return approvedDaiAmount > 0 && approvedDaiAmount >= depositAmount;
        default:
          const approvedUsdtAmount = Number(approvedUsdt);
          return approvedUsdtAmount > 0 && approvedUsdtAmount >= depositAmount;
      }
    }
  }, [stableCoinApprovalsLoaded, approvedUsdt, approvedUsdc, approvedDai, selectedStableCoin, deposit]);

  const stableCoinBalance = useMemo(() => {
    switch (selectedStableCoin?.value) {
      case 'usdc':
        return Number(usdcBalance) || 0;
      case 'dai':
        return Number(daiBalance) || 0;
      default:
        return Number(usdtBalance) || 0;
    }
  }, [selectedStableCoin, usdcBalance, usdtBalance, daiBalance]);

  const archHasBeenApproved = useMemo(() => {
    if (useArchFromWallet) {
      return Number(approvedArch) > Number(tokenFeeInArch);
    }
  }, [useArchFromWallet, tokenFeeInArch, approvedArch]);

  const approveStableCoinButtonText = useMemo(() => {
    if (selectedStableCoin) {
      if (stableHasBeenApproved) return `${selectedStableCoin.label.toUpperCase()} Approved`;
      return `Approve ${selectedStableCoin.label.toUpperCase()}`;
    }
  }, [selectedStableCoin, stableHasBeenApproved]);

  const approveArchButtonText = useMemo(() => {
    if (archHasBeenApproved) return 'ARCH Approved';
    return `Approve ARCH`;
  }, [useArchFromWallet, archHasBeenApproved]);

  const handleApproveClick = async (approvingArch?: boolean) => {
    try {
      const tokenToApprove = approvingArch ? 'arch' : selectedStableCoin.value;
      const amountToApprove = approvingArch ? tokenFeeInArch.toString() : deposit;
      if (approvingArch) {
        setApprovalArchLoading(true);
      } else {
        setApprovalStableCoinLoading(true);
      }
      const res = await approveToken(tokenToApprove, amountToApprove);
      if (res) await updateApprovalStatus();
      if (approvingArch) {
        setApprovalArchLoading(false);
      } else {
        setApprovalStableCoinLoading(false);
      }
    } catch (err) {
      console.log('handleApproveClick err: ', err);
      if (approvingArch) {
        setApprovalArchLoading(false);
      } else {
        setApprovalStableCoinLoading(false);
      }
    }
  };

  const handleStableCoinOnChange = (option: Partial<StableCoinOption>) => {
    setSelectedStableCoin(option);
    setErrorType(null);
  };

  const handleOpenPositionClick = async () => {
    try {
      setOpenPositionLoading(true);
      await openPosition(
        selectedStableCoin.value,
        deposit,
        numOfCycles,
        tokenFeeInArch.toString(),
        collateral.toString(),
        960,
        selectedStableCoin.address,
        useArchFromWallet,
      );
      setOpenPositionLoading(false);
    } catch (err) {
      console.log('handleOpenPositionClick err: ', err);
      setOpenPositionLoading(false);
    }
  };

  const handleErrorType = () => {
    if (isConnected) {
      const depositAmount = Number(deposit);
      if (depositAmount === minimumDepositIncreased) setErrorType(null);
      if (depositAmount <= Number(minimumDeposit)) setErrorType('depositLessThanMinimum');
      if (depositGreaterThanBalance) setErrorType('depositGreaterThanBalance');
    }
  };

  useEffect(() => {
    setSelectedStableCoin(stableCoinOptions[0]);
    setSelectedStrategy(strategyOptions[0]);
    setSelectedLeverage(leverageOptions[0]);
  }, []);

  useEffect(() => {
    setSelectedLeverage(leverageOptions[0]);
  }, [leverageOptions]);

  useEffect(() => {
    setFormError(!!errorType);
  }, [errorType]);

  useEffect(() => {
    handleErrorType();
  }, [deposit, depositGreaterThanBalance, selectedStableCoin]);

  return (
    <Card>
      <Title>
        <span>Deposit</span>
        <MaxButton onClick={maxHandler} disabled={stableCoinBalance === 0}>
          MAX
        </MaxButton>
      </Title>
      <Field variant={formError ? 'error' : 'blue'} includeError={formError}>
        {formError && <StyledAlertIcon />}
        <InputContainer>
          <Input onFocus={(event) => event.target.select()} onChange={handleDepositChange} value={deposit} />
        </InputContainer>
        <DropdownContainer>
          <DropdownComponent
            name="stableCoins"
            options={stableCoinOptions}
            onChange={handleStableCoinOnChange}
            selectedOption={selectedStableCoin}
            id="stable-coin-open-position"
          />
        </DropdownContainer>
      </Field>
      {errorType && <ErrorMessage type={errorType} selectedStableCoin={selectedStableCoin} />}
      <Title>Your Estimated Collateral</Title>
      <Field>
        <InputContainer>
          <Input value={collateral} readOnly />
        </InputContainer>
        <StrategyAndLeverageContainer>
          <DropdownContainer size="medium">
            <Tooltip position="right" name="Strategies">
              <p>OUSD is our primary strategy. Additional strategies coming soon!</p>
            </Tooltip>
            <DropdownComponent
              name="strategies"
              options={strategyOptions}
              onChange={(option: Partial<StableCoinOption>) => setSelectedStrategy(option)}
              selectedOption={selectedStrategy}
              id="strategy-open-position"
              disabled
            />
          </DropdownContainer>
          <DropdownContainer withMarginLeft size="small">
            <Tooltip position="right" name="Strategies">
              <p>Leverage is currently fixed. The ability to adjust the leverage is coming soon!</p>
            </Tooltip>
            <DropdownComponent
              name="leverageValue"
              options={leverageOptions}
              onChange={(option: Partial<StableCoinOption>) => setSelectedLeverage(option)}
              selectedOption={selectedLeverage}
              id="leverage-open-position"
              disabled
            />
          </DropdownContainer>
        </StrategyAndLeverageContainer>
      </Field>
      {balanceArch > tokenFeeInArch && tokenFeeInArch > 0 && (
        <TokensFromWallet
          tokenFeeInArch={tokenFeeInArch}
          setUseArchFromWallet={setUseArchFromWallet}
          useArchFromWallet={useArchFromWallet}
        />
      )}
      <RouteFeeButton onClick={openRouteFeeModal}>
        Route &amp; Fee Details
        <ArrowLeftRight />
      </RouteFeeButton>
      {useArchFromWallet && balanceArch > tokenFeeInArch && (
        <ButtonContainer>
          <Button
            full
            disabled={(currentError || formError || !isConnected || !archApprovalLoaded) && !archHasBeenApproved}
            variant={archHasBeenApproved ? 'done' : 'primary'}
            loading={approvalArchLoading}
            onClick={() => handleApproveClick(true)}
          >
            {approveArchButtonText}
          </Button>
        </ButtonContainer>
      )}
      <ButtonContainer>
        <Button
          full
          disabled={(currentError || formError || Number(deposit) === 0 || !isConnected) && !stableHasBeenApproved}
          variant={stableHasBeenApproved ? 'done' : null}
          loading={approvalStableCoinLoading}
          onClick={() => handleApproveClick()}
        >
          {approveStableCoinButtonText}
        </Button>
      </ButtonContainer>
      <ButtonContainer>
        <Button
          full
          disabled={
            currentError ||
            formError ||
            Number(deposit) === 0 ||
            !stableHasBeenApproved ||
            !isConnected ||
            (useArchFromWallet && !archHasBeenApproved)
          }
          loading={openPositionLoading}
          onClick={handleOpenPositionClick}
        >
          Open Position
        </Button>
      </ButtonContainer>
    </Card>
  );
}
